import { useState, type MouseEvent, type ReactNode, useEffect } from "react"
import cc from "classnames"

import useLink, { type LinkPage } from "@hooks/useLink"

import { ReactComponent as ExternalLink } from "@icons/external-link.svg"

const STYLES = {
  none: "",
  link: "hover:underline mx-16 font-semibold",
  active: "font-semibold hover:border-b hover:border-kiwi-light",
  hollow: "font-semibold h-32 px-12 flex items-center text-14 border rounded-8 border-neutral-200",
  primaryButton: "h-32 px-12 text-14 rounded-8 bg-black text-white flex items-center hover:bg-black-hover",
  primary: "underline hover:no-underline leading-[120%]",
}

export interface LinkProps {
  href?: string | null
  children?: ReactNode
  class_name?: string
  // activeLinks get highlighted when they link to the page currently being viewed
  style?: "link" | "none" | "hollow" | "disabled" | "primaryButton" | "primary"
  new_tab?: boolean
  icon?: boolean
  page?: LinkPage
  recipe?: Domain.Recipe.DataTransferObjects.RecipeData
  user?: any
  tag?: any
  story?: any
  collection?: any
  brand?: any
  bundle?: any
  title?: string
  "data-cy"?: string
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
  query?: Record<string, string>
}

const Link = (props: LinkProps): JSX.Element => {
  const { children, style = "none", new_tab } = props
  const { href } = useLink(props)

  // we need to update the url to include the search parameters once we have them on the client
  const [url, setUrl] = useState(href)
  useEffect(() => {
    const searchParams = new URLSearchParams(Object.entries(props.query ?? {}))
    const computedUrl = `${props.href ?? href}${searchParams.size ? `?${searchParams.toString()}` : ""}`
    setUrl(computedUrl)
  }, [props.href, props.query, href])

  if (style === "disabled") return <>{children}</>

  const className = cc(style && STYLES[style], props.class_name)

  return (
    <a
      href={url}
      className={className}
      {...(new_tab && { target: "_blank", rel: "noopener noreferrer" })}
      onClick={props.onClick}
      title={props.title}
      data-cy={props["data-cy"]}
    >
      {children}
      {props.icon && (
        <div className="ml-4 inline-block h-16 w-16 align-middle">
          <ExternalLink />
        </div>
      )}
    </a>
  )
}

export default Link
